import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SupportedLanguage, Translations, getLanguageFromCountry, getTranslations, interpolate } from '../lib/i18n';

interface LocalizationContextType {
  language: SupportedLanguage;
  translations: Translations;
  t: (key: keyof Translations, values?: Record<string, string | number>) => string;
  setLanguageFromCountry: (countryCode: string) => void;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

interface LocalizationProviderProps {
  children: ReactNode;
  initialCountry?: string;
}

export function LocalizationProvider({ children, initialCountry = 'US' }: LocalizationProviderProps) {
  const [language, setLanguage] = useState<SupportedLanguage>(() => {
    // Initialize language from localStorage or initial country
    const storedCountry = localStorage.getItem('selectedCountry') || initialCountry;
    return getLanguageFromCountry(storedCountry);
  });

  const [translations, setTranslations] = useState<Translations>(() => {
    return getTranslations(language);
  });

  // Update translations when language changes
  useEffect(() => {
    setTranslations(getTranslations(language));
    //console.log('🌐 Language changed to:', language);
  }, [language]);

  // Function to set language based on country code
  const setLanguageFromCountry = (countryCode: string) => {
    const newLanguage = getLanguageFromCountry(countryCode);
    if (newLanguage !== language) {
      //console.log('🌐 Setting language from country:', countryCode, '->', newLanguage);
      setLanguage(newLanguage);
    }
  };

  // Translation function with interpolation support
  const t = (key: keyof Translations, values?: Record<string, string | number>): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`🌐 Missing translation for key: ${key} in language: ${language}`);
      return key;
    }

    if (values) {
      return interpolate(translation, values);
    }

    return translation;
  };

  const contextValue: LocalizationContextType = {
    language,
    translations,
    t,
    setLanguageFromCountry,
  };

  return (
    <LocalizationContext.Provider value={contextValue}>
      {children}
    </LocalizationContext.Provider>
  );
}

// Hook to use localization
export function useLocalization(): LocalizationContextType {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}

// Hook to get just the translation function (convenience)
export function useTranslation() {
  const { t } = useLocalization();
  return t;
}